/**
 * TrialUrgencyBanner — Top-of-page countdown banner (≤3 days left).
 *
 * Dismissible per day via sessionStorage. Reappears each new day/session.
 */

import { useMemo, useState } from "react"
import { useNavigate } from "react-router"
import { Clock, X, ArrowRight } from "lucide-react"
import { Button } from "@/components/ui/button"
import { usePlanContext } from "@/lib/usePlanContext"

export function TrialUrgencyBanner() {
  const { isTrialing, trialEndsAt } = usePlanContext()
  const navigate = useNavigate()

  const daysLeft = useMemo(() => {
    if (!trialEndsAt) return null
    const end = new Date(trialEndsAt)
    const now = new Date()
    const diffMs = end.getTime() - now.getTime()
    if (diffMs <= 0) return 0
    return Math.ceil(diffMs / (1000 * 60 * 60 * 24))
  }, [trialEndsAt])

  const todayKey = `trialBannerDismissed-${new Date().toISOString().slice(0, 10)}`
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(todayKey) === "true"
    } catch {
      return false
    }
  })

  // Only show during active trial with ≤3 days left
  if (!isTrialing || daysLeft === null || daysLeft > 3 || dismissed) return null

  const handleDismiss = () => {
    try {
      sessionStorage.setItem(todayKey, "true")
    } catch {
      // sessionStorage unavailable
    }
    setDismissed(true)
  }

  return (
    <div className="bg-warning/10 border border-warning/30 rounded-lg px-4 py-3 mx-8 mt-4 mb-0">
      <div className="flex items-start gap-3">
        <Clock className="h-4 w-4 text-warning mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <p className="text-sm font-medium text-foreground">
            Your Concierge trial ends in {daysLeft === 1 ? "1 day" : `${daysLeft} days`}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            Subscribe now to keep AI narratives, review intelligence, and guest voice.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button size="sm" onClick={() => navigate("settings?tab=billing")}>
            Keep Concierge
            <ArrowRight className="h-3.5 w-3.5 ml-1" />
          </Button>
          <Button size="sm" variant="ghost" onClick={handleDismiss}>
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </div>
  )
}
